import React, { useCallback, useState } from 'react';
import styled from 'astroturf/react';

import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';

import RatingBase, { StarValue } from './Rating';

const Container = styled.div<{ variant: 'default' | 'corex' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--12, 12px) var(--12, 12px) var(--12, 12px) var(--16, 16px);
  gap: var(--16, 16px);
  flex-wrap: wrap;

  border-radius: var(--4, 4px);
  border: 1px solid var(--Blue-Secondary-3, #cddaef);
  background: var(--Blue-Secondary-1, #eef2f9);

  /* Shadow 1 */
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.08);

  &.variant-corex {
    margin: 8px;
  }
`;

const MessageContainer = styled.div`
  flex: 1 0 10em;
`;

const Message = styled.p`
  margin: 0;
  color: var(--Monochromatic-Black, #223);

  /* Body/Small/Regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const Note = styled.p`
  margin: 0;
  color: var(--Neutral-90, #535366);

  /* Body/XSmall/Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 150% */
`;

const Rating = styled(RatingBase)`
  margin-left: 9px;
  margin-right: 9px;
`;

type Props = {
  className?: string;
  newsType: string;
  newsId: string | number;
  reason?: string;
  variant?: 'default' | 'corex';
};

const RatingCard: React.FC<Props> = ({
  className,
  newsType,
  newsId,
  reason,
  variant = 'default',
}) => {
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [stars, setStars] = useState<StarValue | undefined>(undefined);

  const onChange = useCallback(
    (value: StarValue) => {
      setSubmitted(true);
      setStars(value);
      arbitraryEvents.write('SocialRecommendationRating', {
        rating: value,
        news_type: newsType,
        news_id: newsId,
        reason: reason || '',
      });
    },
    [newsId, newsType, reason],
  );

  const message = submitted ? (
    <Message>Thanks for your feedback</Message>
  ) : (
    <>
      <Message>How relevant is this for you?</Message>
      <Note>Your response is private</Note>
    </>
  );

  return (
    <Container className={className} variant={variant}>
      <MessageContainer>{message}</MessageContainer>
      <Rating stars={stars} onChange={onChange} />
    </Container>
  );
};

export default RatingCard;
